import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, FAKE, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, Btn, Badge, Progress, Card, ExportMenu } from "../shared/components"
import { getMyProgress } from "../api/courses.api"
import { downloadLearningGainXlsx, downloadLearningGainPdf } from "../api/users.api"
import {
  getTest,
  submitTest as submitTestApi,
  getLearningGain,
  getCohortLearningGain,
  getPendingGrading,
  getAttempt,
  gradeEssays,
  manualScoreTest,
} from "../api/tests.api"

/* ============================================================
   PRD §FR-6: Pretest & Posttest + Learning Gain Dashboard
   Exposes: TestScreen, LearningGainScreen
   ============================================================ */

// Ambang kelulusan posttest, sama dengan POSTTEST_THRESHOLD di backend
// (services/enrollment.service.js) — di sanalah kelayakan sertifikat diputuskan.
const NILAI_LULUS = 70;
// Target kenaikan pretest→posttest. Menurut PRD ini target aspiratif untuk
// ditampilkan, TIDAK ditegakkan sistem — jadi tidak boleh disajikan sebagai
// syarat yang gagal/lulus, cukup sebagai sasaran.
const TARGET_GAIN = 20;
// Ambang kehadiran. Bersama NILAI_LULUS inilah SATU-SATUNYA penentu kelayakan
// sertifikat di server (enrollment.service.js): kehadiran ≥ 80% DAN posttest
// ≥ 70. Pretest dan kelengkapan materi TIDAK ikut menentukan — menampilkannya
// sebagai syarat membuat peserta mengira dirinya terhalang padahal tidak.
const AMBANG_HADIR = 80;

// Satu baris syarat: tercapai, belum tercapai, atau belum bisa dinilai.
// Ketiganya harus bisa dibedakan sekilas tanpa membaca angkanya.
function Syarat({ terpenuhi, menunggu, label, nilai, lang }) {
  return (
    <div className="row gap-2 small">
      {menunggu
        ? <span className="dot-status amber" title={lang==="id"?"Belum dikerjakan":"Not taken yet"}/>
        : terpenuhi
          ? <Icon name="check" size={14} color="var(--green-60)"/>
          : <Icon name="close" size={14} color="var(--text-3)"/>}
      <span className="muted">{label}</span>
      <span className="mono" style={{color: menunggu ? "var(--text-3)" : terpenuhi ? "var(--green-60)" : "var(--text)"}}>
        {nilai}
      </span>
    </div>
  );
}

// Jejak perjalanan — orientasi, sengaja DIPISAHKAN dari daftar syarat: materi
// dan pretest tidak menentukan kelayakan sertifikat, jadi keduanya hanya boleh
// muncul sebagai kemajuan.
//
// Dipakai layar pretest DAN posttest. `post` bernilai null saat peserta baru
// menyelesaikan pretest — itu benar, dan memang harus tampil sebagai langkah
// yang belum dijalani.
function JejakPerjalanan({ pre, materiPct, post, layak, isMobile, lang }) {
  const langkah = [
    { l: "Pretest", ok: pre != null, v: pre != null ? String(pre) : "—" },
    { l: lang === "id" ? "Materi" : "Materials", ok: materiPct >= 100, v: `${materiPct}%` },
    { l: "Posttest", ok: post != null, v: post != null ? String(post) : "—" },
    {
      l: lang === "id" ? "Sertifikat" : "Certificate",
      ok: layak,
      v: layak ? (lang === "id" ? "Siap" : "Ready") : (lang === "id" ? "Menunggu" : "Pending"),
    },
  ];
  return (
    <div className="mt-4">
      <div className="small" style={{ fontWeight: 600, marginBottom: 10 }}>
        {lang === "id" ? "Perjalanan Anda" : "Your progress"}
      </div>
      <div
        className="row gap-2"
        style={{ flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "center" }}
      >
        {langkah.map((s, i, arr) => (
          <React.Fragment key={s.l}>
            <div className="row gap-2" style={{ alignItems: "center" }}>
              <Icon name={s.ok ? "check" : "clock"} size={14} color={s.ok ? "var(--green-60)" : "var(--text-3)"} />
              <span className="small" style={{ color: s.ok ? "var(--text)" : "var(--text-3)" }}>{s.l}</span>
              <span className="mono tiny muted">{s.v}</span>
            </div>
            {!isMobile && i < arr.length - 1 && (
              <span style={{ flex: 1, height: 1, background: "var(--border)", minWidth: 12 }} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// Panel skor pretest. Padanan "nyatakan vonisnya" di posttest, tapi isinya
// berkebalikan: di sini justru menyatakan bahwa TIDAK ADA vonis.
//
// Pretest tidak punya ambang kelulusan — kelayakan sertifikat ditentukan
// kehadiran ≥80% dan posttest ≥70 saja. Peserta yang mendapat 30 mudah mengira
// dirinya sudah gagal pelatihan, padahal pretest memang dikerjakan SEBELUM
// belajar apa pun sehingga skor rendah itu wajar. Aplikasi sebelumnya tidak
// mengatakan apa pun soal itu.
//
// Warnanya netral, bukan hijau/kuning: keduanya menyiratkan penilaian yang
// tidak ada di sini.
function SkorAwalPretest({ skor, lang }) {
  return (
    <div
      className="mt-4"
      style={{
        border: "1px solid var(--border)",
        background: "var(--program-color-soft)",
        borderRadius: "var(--r-md)",
        padding: "20px 24px",
      }}
    >
      <div className="row gap-3" style={{ alignItems: "baseline", justifyContent: "center", flexWrap: "wrap" }}>
        <div className="mono" style={{ fontSize: 44, fontWeight: 300, lineHeight: 1, color: "var(--program-color)" }}>
          {skor}<span style={{ fontSize: 16, color: "var(--text-3)" }}>/100</span>
        </div>
        <Badge>{lang === "id" ? "Skor awal" : "Baseline"}</Badge>
      </div>
      <div className="small muted mt-2" style={{ textAlign: "center" }}>
        {lang === "id"
          ? "Ini bukan ujian kelulusan. Skor ini titik awal untuk mengukur kemajuan Anda di posttest nanti — nilai rendah wajar, karena pretest dikerjakan sebelum materi dipelajari."
          : "This isn't a pass/fail exam. It's the baseline your progress is measured against at the posttest — a low score is normal, since the pretest is taken before you study the materials."}
      </div>
    </div>
  );
}

// Panel setelah pretest: jejak perjalanan + harapan ke depan.
//
// SENGAJA bukan daftar "Syarat sertifikat" bercentang seperti di posttest. Di
// titik ini kedua syarat pasti belum terpenuhi, dan menampilkan dua tanda ✗
// tepat setelah peserta menyelesaikan langkah pertamanya mematahkan semangat
// tanpa memberi guna apa pun.
//
// Kehadiran disebut di sini karena inilah satu-satunya saat menyebutnya masih
// berguna: peserta yang tahu sejak pagi akan memindai QR di tiap sesi. Yang
// baru tahu di akhir sudah terlambat — kehadiran tidak bisa diperbaiki surut.
function RingkasanSetelahPretest({ data, skorPretest, isMobile, lang }) {
  if (!data) {
    return (
      <div className="small muted mt-4" style={{ textAlign: "center" }}>
        {lang === "id" ? "Memuat ringkasan…" : "Loading summary…"}
      </div>
    );
  }
  const materiPct = data.prog?.pct != null ? Math.round(data.prog.pct) : 0;
  return (
    <div className="mt-5" style={{ textAlign: "left" }}>
      <JejakPerjalanan
        pre={skorPretest}
        materiPct={materiPct}
        post={null}
        layak={data.lg?.certEligible === true}
        isMobile={isMobile}
        lang={lang}
      />
      <div className="divider mt-4" />
      <div className="small muted mt-4">
        {lang === "id"
          ? `Untuk sertifikat nanti: posttest ≥ ${NILAI_LULUS} dan kehadiran ≥ ${AMBANG_HADIR}%. Pastikan Anda memindai QR di setiap sesi — kehadiran tidak bisa ditambahkan setelah sesi berlalu.`
          : `For the certificate later: posttest ≥ ${NILAI_LULUS} and attendance ≥ ${AMBANG_HADIR}%. Make sure you scan the QR at every session — attendance can't be added after a session has passed.`}
      </div>
    </div>
  );
}

// Vonis posttest: angka BESERTA artinya. Angka telanjang tidak menjawab
// pertanyaan yang sebenarnya ada di kepala peserta — "saya lulus atau tidak?".
function VonisPosttest({ skor, esaiMenunggu, lang }) {
  const lulus = skor >= NILAI_LULUS;
  return (
    <div
      className="mt-4"
      style={{
        border: "1px solid",
        borderColor: lulus ? "var(--green-60)" : "var(--amber-50)",
        background: lulus ? "var(--success-soft)" : "var(--amber-10, #FFF8E6)",
        borderRadius: "var(--r-md)",
        padding: "20px 24px",
      }}
    >
      <div className="row gap-3" style={{ alignItems: "baseline", justifyContent: "center", flexWrap: "wrap" }}>
        <div className="mono" style={{ fontSize: 44, fontWeight: 300, lineHeight: 1, color: lulus ? "var(--green-60)" : "var(--amber-50)" }}>
          {skor}<span style={{ fontSize: 16, color: "var(--text-3)" }}>/100</span>
        </div>
        <Badge tone={lulus ? "success" : "warning"} dot>
          {lulus
            ? (lang === "id" ? "Lulus" : "Passed")
            : (lang === "id" ? `Belum mencapai ${NILAI_LULUS}` : `Below ${NILAI_LULUS}`)}
        </Badge>
      </div>
      <div className="small muted mt-2" style={{ textAlign: "center" }}>
        {esaiMenunggu
          ? (lang === "id"
            ? `Skor sementara — esai belum dinilai, jadi nilai akhir masih bisa NAIK. Ambang lulus ${NILAI_LULUS}.`
            : `Provisional — the essay isn't graded yet, so your final score can only go UP. Pass mark is ${NILAI_LULUS}.`)
          : (lang === "id"
            ? `Ambang lulus ${NILAI_LULUS}.`
            : `Pass mark is ${NILAI_LULUS}.`)}
      </div>
    </div>
  );
}

// Panel "lalu apa?" — mengisi ruang yang dulu kosong dengan hal yang memang
// dicari peserta setelah menekan kirim.
//
// Syaratnya SENGAJA hanya dua. Kelayakan sertifikat di server ditentukan
// kehadiran ≥ 80% DAN posttest ≥ 70; pretest dan kelengkapan materi tidak ikut.
// Kartu Sertifikat di dasbor menampilkan empat baris dan karenanya menyiratkan
// syarat yang tidak ada. Jejak perjalanan di bawahnya memuat materi/pretest —
// tapi sebagai KEMAJUAN, bukan sebagai syarat.
function RingkasanSetelahPosttest({ data, skorPosttest, isMobile, lang }) {
  if (!data) {
    return (
      <div className="small muted mt-4" style={{ textAlign: "center" }}>
        {lang === "id" ? "Memuat ringkasan…" : "Loading summary…"}
      </div>
    );
  }
  const lg = data.lg || {};
  const prog = data.prog || {};
  const pre = lg.pretest != null ? Math.round(lg.pretest) : null;
  const hadir = lg.attendancePct != null ? Math.round(lg.attendancePct) : null;
  const materiPct = prog.pct != null ? Math.round(prog.pct) : 0;

  const lulusPosttest = skorPosttest >= NILAI_LULUS;
  const hadirCukup = hadir != null && hadir >= AMBANG_HADIR;
  // Status akhir diambil dari server, bukan dihitung ulang di sini: kelayakan
  // bersifat satu arah (sekali menyala tidak padam), sehingga peserta bisa saja
  // sudah layak walau angkanya sekarang di bawah ambang.
  const layak = lg.certEligible === true;

  return (
    <div className="mt-5" style={{ textAlign: "left" }}>
      {/* Learning gain di tempat — dulu peserta harus menekan tombol untuk
          melihat angka yang sudah tersedia. */}
      {pre != null && (
        <div className="row gap-3 mb-4" style={{ alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
          <span className="small muted">{lang === "id" ? "Pretest" : "Pretest"}</span>
          <span className="mono" style={{ fontSize: 20, color: "var(--text-secondary)" }}>{pre}</span>
          <Icon name="arrow_right" size={14} color="var(--text-tertiary)" />
          <span className="small muted">{lang === "id" ? "Posttest" : "Posttest"}</span>
          <span className="mono" style={{ fontSize: 20 }}>{skorPosttest}</span>
          <Badge tone={skorPosttest - pre >= TARGET_GAIN ? "success" : undefined}>
            {skorPosttest - pre >= 0 ? "+" : ""}{skorPosttest - pre}
          </Badge>
        </div>
      )}

      <div className="divider" />

      <div className="mt-4">
        <div className="small" style={{ fontWeight: 600, marginBottom: 10 }}>
          {lang === "id" ? "Syarat sertifikat" : "Certificate requirements"}
        </div>
        <div className="col gap-2">
          <Syarat
            terpenuhi={lulusPosttest}
            menunggu={false}
            label={lang === "id" ? `Posttest ≥ ${NILAI_LULUS}` : `Posttest ≥ ${NILAI_LULUS}`}
            nilai={String(skorPosttest)}
            lang={lang}
          />
          <Syarat
            terpenuhi={hadirCukup}
            menunggu={hadir == null}
            label={lang === "id" ? `Kehadiran ≥ ${AMBANG_HADIR}%` : `Attendance ≥ ${AMBANG_HADIR}%`}
            nilai={hadir == null ? "—" : `${hadir}%`}
            lang={lang}
          />
        </div>
        <div className="small mt-3" style={{ color: layak ? "var(--green-60)" : "var(--text-secondary)" }}>
          {layak
            ? (lang === "id"
              ? "Anda sudah memenuhi syarat. Tunggu kode klaim dari penyelenggara."
              : "You already qualify. Wait for the claim code from the organiser.")
            : !lulusPosttest
              ? (lang === "id"
                ? "Skor posttest belum mencapai ambang. Hubungi trainer bila ada kesempatan mengulang."
                : "Your posttest is below the pass mark. Ask your trainer about a retake.")
              : (lang === "id"
                ? "Skor sudah cukup. Kehadiran yang masih kurang — pastikan Anda memindai QR di setiap sesi."
                : "Your score is enough. Attendance is what's missing — make sure you scan the QR at every session.")}
        </div>
      </div>

      <div className="divider mt-4" />

      <JejakPerjalanan
        pre={pre}
        materiPct={materiPct}
        post={skorPosttest}
        layak={layak}
        isMobile={isMobile}
        lang={lang}
      />
    </div>
  );
}

// =============================================================
// TEST SCREEN — used for both Pretest & Posttest
// =============================================================
function TestScreen() {
  const t = useT();
  const { lang, navigate, pageState, currentProgramId, currentProgram } = useApp();
  const orgName = useOrgName()
  const isMobile = useIsMobile();
  const kind = pageState?.kind || "pretest"; // 'pretest' | 'posttest'
  const isPretest = kind === "pretest";

  const programLabel = currentProgram?.name || orgName;
  const meta = {
    pretest: {
      titleId: `Pretest — ${programLabel}`, titleEn: `Pretest — ${programLabel}`,
      subId: "Skor awal Anda direkam sebagai baseline. Tidak ada nilai minimum; jawab sebaik mungkin.",
      subEn: "Your baseline score is recorded. No pass threshold; just answer as best you can.",
      next: "course",
    },
    posttest: {
      titleId: `Posttest — ${programLabel}`, titleEn: `Posttest — ${programLabel}`,
      subId: "Penilaian akhir. Skor minimum 70 untuk lulus.",
      subEn: "Final assessment. Minimum score 70 to pass.",
      next: "learning-gain",
    },
  }[kind];

  const [test, setTest] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [active, setActive] = React.useState(0);
  const [answers, setAnswers] = React.useState({}); // map questionId → answer
  const [submitted, setSubmitted] = React.useState(false);
  const [submittedResult, setSubmittedResult] = React.useState(null);
  // Gates the timed question view behind an explicit "Start" click. A
  // one-attempt test (pretest) starts consuming that attempt the moment its
  // page opens — without this, someone who navigates here just to check
  // status (e.g. from the dashboard) silently begins their only try.
  const [started, setStarted] = React.useState(false);
  const [timeLeft, setTimeLeft] = React.useState(isPretest ? 20 * 60 : 30 * 60);
  // Alasan sebenarnya dari server saat tes tidak bisa dibuka. Tanpa ini semua
  // kegagalan tampil sebagai "belum tersedia untuk program ini", padahal yang
  // paling sering terjadi adalah posttest yang masih terkunci — dan peserta
  // butuh tahu bahwa yang kurang adalah modulnya, bukan tesnya yang tidak ada.
  const [halangan, setHalangan] = React.useState(null);

  React.useEffect(() => {
    if (!currentProgramId) return;
    setLoading(true);
    getTest(currentProgramId, kind)
      .then((data) => {
        setTest(data);
        if (data.alreadySubmitted) {
          setSubmitted(true);
          setSubmittedResult({ score: data.previousScore, alreadySubmitted: true });
        }
      })
      .catch((err) => {
        const data = err?.response?.data;
        setHalangan({
          pesan: data?.error || err.message,
          terkunci: !!data?.locked,
        });
        toast.error(data?.error || err.message);
      })
      .finally(() => setLoading(false));
  }, [currentProgramId, kind]);

  // Map API questions into local format for rendering (keep MCQ/ESSAY shape)
  const questions = React.useMemo(() => {
    if (!test?.questions) return [];
    return test.questions.map((q) => ({
      id: q.id,
      q: q.body,
      type: q.type === "MCQ" ? "mcq" : "essay",
      opts: (q.options || []).map((o) => o.text),
      optKeys: (q.options || []).map((o) => o.key),
    }));
  }, [test]);

  async function handleSubmit() {
    try {
      // Convert answers (indexed by questionId) into key-by-id format for API
      const payload = {};
      for (const q of questions) {
        const a = answers[q.id];
        if (a === undefined || a === "") continue;
        // MCQ: store option key, ESSAY: store text
        payload[q.id] = q.type === "mcq" ? (q.optKeys[a] ?? String(a)) : a;
      }
      const result = await submitTestApi(currentProgramId, kind, payload);
      setSubmittedResult(result);
      setSubmitted(true);
      toast.success(lang === "id" ? "Jawaban terkirim" : "Submitted");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message);
    }
  }

  React.useEffect(() => {
    if (submitted || !started) return;
    const id = setInterval(() => setTimeLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [submitted, started]);

  // Setelah tes terkirim, peserta ingin tahu di mana posisinya. Dua panggilan
  // ini yang menjawabnya — skor pretest untuk learning gain, kehadiran untuk
  // syarat sertifikat, dan progres materi untuk jejak perjalanan.
  //
  // Dipakai kedua tes: pretest memakainya untuk jejak perjalanan dan angka
  // kehadiran, posttest untuk itu semua ditambah daftar syarat.
  const [ringkasan, setRingkasan] = React.useState(null);
  React.useEffect(() => {
    if (!submitted || !currentProgramId) return;
    Promise.all([
      getLearningGain(currentProgramId).catch(() => null),
      getMyProgress(currentProgramId).catch(() => null),
    ]).then(([lg, prog]) => setRingkasan({ lg, prog }));
  }, [submitted, currentProgramId]);

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");
  const answered = Object.keys(answers).length;

  if (loading) {
    return (
      <div className="main-inner" style={{maxWidth: 720, paddingTop: 80, textAlign: "center"}}>
        <p className="muted">{lang === "id" ? "Memuat soal…" : "Loading questions…"}</p>
      </div>
    );
  }

  if (!test || questions.length === 0) {
    return (
      <div className="main-inner" style={{maxWidth: 720, paddingTop: 80, textAlign: "center"}}>
        <p className="muted">
          {halangan?.pesan
            || (lang === "id" ? "Tes belum tersedia untuk program ini." : "Test not available for this program.")}
        </p>
        {halangan?.terkunci && (
          <div style={{ marginTop: 16 }}>
            <Btn variant="primary" icon={<Icon name="play" size={14} />} onClick={() => navigate("courses")}>
              {lang === "id" ? "Buka materi" : "Open materials"}
            </Btn>
          </div>
        )}
      </div>
    );
  }

  if (submitted) {
    // While an essay is pending, `score` is null (final score isn't known
    // yet) but `partialScoreFromMcq` already is — prefer it so "Provisional"
    // doesn't show 0/100 right after a participant answers every MCQ correctly.
    // For essay-less tests the two are identical, so this never regresses.
    const score = submittedResult?.partialScoreFromMcq ?? submittedResult?.score ?? 0;
    const essayCount = questions.filter(q => q.type === "essay").length;
    const provisional = Math.round(score);
    const essayPending = essayCount > 0;

    return (
      <div className="main-inner" style={{maxWidth: 720}}>
        <div className="card" style={{padding: 32, textAlign: "center"}}>
          {/* Ikonnya ikut hasil, bukan selalu centang hijau. Sebelumnya peserta
              yang mendapat 60 tetap disambut centang hijau besar — sinyal
              perayaan yang berlawanan dengan kenyataan, dan itu yang paling
              dulu dilihat mata sebelum angkanya terbaca. Pretest tidak punya
              ambang lulus, jadi di sana centang tetap tepat: yang dirayakan
              adalah "terkirim", bukan "lulus". */}
          {(() => {
            const rayakan = isPretest || provisional >= NILAI_LULUS;
            return (
              <div style={{width:80, height:80, borderRadius:"50%", background: isPretest ? "var(--program-color-soft)" : rayakan ? "var(--success-soft)" : "var(--amber-10, #FFF8E6)", display:"inline-grid", placeItems:"center", marginBottom: 16}}>
                <Icon
                  name={rayakan ? "check" : "alert"}
                  size={36}
                  color={isPretest ? "var(--program-color)" : rayakan ? "var(--success)" : "var(--amber-50)"}
                />
              </div>
            );
          })()}
          <h1 className="h1" style={{fontSize: 28}}>{isPretest
            ? (lang==="id"?"Pretest terkirim":"Pretest submitted")
            : (lang==="id"?"Posttest terkirim":"Posttest submitted")}</h1>
          <p className="muted mt-2">
            {isPretest
              ? (lang==="id"?"Skor baseline Anda tersimpan. Materi kursus sekarang terbuka.":"Your baseline is saved. Course materials are now unlocked.")
              : (lang==="id"?"Soal pilihan ganda dinilai otomatis. Soal esai menunggu penilaian trainer.":"Multiple-choice answers were auto-graded. Essay awaits trainer review.")}
          </p>

          {/* VONIS. Dulu di sini ada tiga kartu: "Soal 20" (trivia yang tidak
              dibutuhkan peserta), "Skor sementara" berupa angka telanjang, dan
              "Esai". Angkanya tidak pernah dibandingkan dengan ambang lulus —
              peserta yang mendapat 60 melihat centang hijau besar dan angka 60,
              tanpa satu pun tanda bahwa ia belum lulus.

              Esai yang belum dinilai dihitung NOL oleh partialScoreFromMcq
              (poin PG dibagi TOTAL poin), jadi skor akhir hanya bisa NAIK.
              Karena itu "Lulus" aman dinyatakan begitu provisional ≥ 70, dan
              yang di bawahnya disertai keterangan bahwa nilainya masih bisa
              naik — akurat sekaligus tidak menakuti. */}
          {isPretest ? (
            <SkorAwalPretest skor={provisional} lang={lang} />
          ) : (
            <VonisPosttest
              skor={provisional}
              esaiMenunggu={essayPending}
              lang={lang}
            />
          )}

          {isPretest ? (
            <RingkasanSetelahPretest
              data={ringkasan}
              skorPretest={provisional}
              isMobile={isMobile}
              lang={lang}
            />
          ) : (
            <RingkasanSetelahPosttest
              data={ringkasan}
              skorPosttest={provisional}
              isMobile={isMobile}
              lang={lang}
            />
          )}

          <div className="row gap-2 mt-5" style={{justifyContent:"center", flexWrap:"wrap"}}>
            {isPretest ? (
              <Btn variant="primary" icon={<Icon name="book" size={14}/>} onClick={()=>navigate("course")}>
                {lang==="id"?"Mulai belajar materi":"Start the materials"}
              </Btn>
            ) : (
              <>
                {/* Aksi maju diutamakan. Dulu "Buka materi kursus" berbobot
                    setara, padahal itu langkah mundur setelah tes terakhir. */}
                <Btn variant="primary" icon={<Icon name="cert" size={14}/>} onClick={()=>navigate("certificates")}>
                  {lang==="id"?"Status sertifikat":"Certificate status"}
                </Btn>
                <Btn variant="secondary" icon={<Icon name="chart" size={14}/>} onClick={()=>navigate("learning-gain")}>
                  {lang==="id"?"Learning Gain":"Learning Gain"}
                </Btn>
                <Btn variant="ghost" size="sm" onClick={()=>navigate("course")}>
                  {lang==="id"?"Kembali ke materi":"Back to materials"}
                </Btn>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!started) {
    const menit = Math.round((isPretest ? 20 * 60 : 30 * 60) / 60);
    return (
      <div className="main-inner" style={{maxWidth: 720, paddingTop: 80}}>
        <div className="card" style={{padding: 32, textAlign: "center"}}>
          <div style={{width:64, height:64, borderRadius:"50%", background: "var(--program-color-soft)", display:"inline-grid", placeItems:"center", marginBottom: 16}}>
            <Icon name="clock" size={28} color="var(--program-color)" />
          </div>
          <h1 className="h1" style={{fontSize: 24}}>{lang === "id" ? meta.titleId : meta.titleEn}</h1>
          <p className="muted mt-2">
            {isPretest
              ? (lang === "id"
                ? `Tes ini dibatasi satu percobaan dan berjalan ${menit} menit sejak Anda memulainya. Setelah dimulai, membuka halaman lain tidak menghentikan waktunya.`
                : `This test allows one attempt and runs for ${menit} minutes once started. Leaving the page won't stop the clock.`)
              : (lang === "id"
                ? `Percobaan tersisa ${Math.max(0, (test.maxAttempts ?? 2) - (test.attemptsUsed ?? 0))} dari ${test.maxAttempts ?? 2}. Tes berjalan ${menit} menit sejak dimulai.`
                : `${Math.max(0, (test.maxAttempts ?? 2) - (test.attemptsUsed ?? 0))} of ${test.maxAttempts ?? 2} attempts remaining. The test runs for ${menit} minutes once started.`)}
          </p>
          <div className="mt-5">
            <Btn variant="primary" icon={<Icon name="play" size={14} />} onClick={() => setStarted(true)}>
              {lang === "id" ? "Mulai tes" : "Start test"}
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  const cur = questions[active];

  return (
    <div className="main-inner" style={{maxWidth: 1280}}>
      {/* Page header */}
      <div className="page-header" style={{marginBottom: 16}}>
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {isPretest ? "Pretest" : "Posttest"}
          </div>
          <h1 className="h1" style={{marginTop: 6}}>{lang === "id" ? meta.titleId : meta.titleEn}</h1>
          <div className="muted mt-1">{lang === "id" ? meta.subId : meta.subEn}</div>
        </div>
        <div className="page-header-actions">
          <div className="col gap-1" style={{alignItems: "flex-end"}}>
            <div className="row gap-2">
              <Badge tone={isPretest ? "brand" : "warning"} dot>
                {isPretest ? t.pretest : t.posttest}
              </Badge>
              {test?.maxAttempts != null && (
                <Badge>
                  {lang === "id" ? "Percobaan" : "Attempt"} {(test.attemptsUsed ?? 0) + 1}/{test.maxAttempts}
                </Badge>
              )}
            </div>
            <div className="mono" style={{fontSize: 28, fontWeight: 300, color: timeLeft < 120 ? "var(--danger)" : "var(--text)"}}>{mm}:{ss}</div>
            <div className="tiny muted mono">{answered}/{questions.length} {lang==="id"?"dijawab":"answered"}</div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <Progress value={Math.round((answered/questions.length)*100)} style={{marginBottom: 16}} />

      <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "1fr 240px", gap: 16}}>
        {/* Question */}
        <div className="card">
          <div className="card-header">
            <div className="h3">{lang==="id"?"Soal":"Question"} {active + 1} <span className="muted" style={{fontWeight:400}}>/ {questions.length}</span></div>
            <Badge>
              {cur.type === "mcq" ? (lang==="id"?"Pilihan ganda · auto":"Multiple-choice") : (lang==="id"?"Esai · trainer":"Essay · trainer")}
            </Badge>
          </div>
          <div style={{padding: "28px 32px"}}>
            <p style={{fontSize: 17, lineHeight: 1.5, margin: 0}}>{cur.q}</p>

            {cur.type === "mcq" && (
              <div className="col gap-2 mt-5">
                {cur.opts.map((opt, i) => {
                  const selected = answers[cur.id] === i;
                  return (
                    <label key={i}
                      className="row gap-3"
                      style={{
                        padding: "14px 16px",
                        border: "1px solid",
                        borderColor: selected ? "var(--blue-60)" : "var(--border)",
                        background: selected ? "var(--blue-10)" : "white",
                        cursor: "pointer",
                      }}
                      onClick={() => setAnswers({...answers, [cur.id]: i})}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: "50%",
                        border: "2px solid",
                        borderColor: selected ? "var(--blue-60)" : "var(--g-40)",
                        display: "grid", placeItems: "center", flexShrink: 0,
                      }}>
                        {selected && <div style={{width: 8, height: 8, borderRadius: "50%", background: "var(--blue-60)"}}/>}
                      </div>
                      <span className="mono tiny" style={{color: "var(--text-tertiary)", width: 16}}>{cur.optKeys[i] || String.fromCharCode(65+i)}.</span>
                      <span style={{flex: 1, fontSize: 14}}>{opt}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {cur.type === "essay" && (
              <div className="mt-5">
                <textarea
                  value={answers[cur.id] || ""}
                  onChange={e => setAnswers({...answers, [cur.id]: e.target.value})}
                  placeholder={lang==="id"?"Tulis jawaban Anda di sini…":"Write your answer here…"}
                  style={{width: "100%", minHeight: 200, padding: "12px 14px", border: "1px solid var(--border-strong)", outline: "none", fontFamily: "inherit", fontSize: 14, resize: "vertical"}}
                />
                <div className="row between mt-2">
                  <span className="tiny muted">{lang==="id"?"Dinilai oleh trainer":"Trainer-graded"}</span>
                  <span className="tiny mono muted">{(answers[cur.id] || "").trim().split(/\s+/).filter(Boolean).length} {lang==="id"?"kata":"words"}</span>
                </div>
              </div>
            )}
          </div>
          <div style={{padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center"}}>
            <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={14}/>} onClick={() => setActive(Math.max(0, active - 1))} disabled={active === 0}>
              {t.back}
            </Btn>
            <div className="row gap-2">
              {active === questions.length - 1 ? (
                <Btn variant="primary" icon={<Icon name="check" size={14}/>} onClick={handleSubmit}>
                  {lang==="id"?"Kirim jawaban":"Submit answers"}
                </Btn>
              ) : (
                <Btn variant="primary" onClick={() => setActive(active + 1)}>
                  {t.next} <Icon name="arrow_right" size={14}/>
                </Btn>
              )}
            </div>
          </div>
        </div>

        {/* Question navigator */}
        <div className="col gap-3">
          <div className="card">
            <div className="card-header"><div className="h3">{lang==="id"?"Navigasi soal":"Question navigator"}</div></div>
            <div style={{padding: 12}}>
              {/* Inline display:grid (no .grid class) so the mobile
                  single-column collapse rule leaves this 5-wide palette intact */}
              <div style={{display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 6}}>
                {questions.map((q, i) => {
                  const isAnswered = answers[q.id] !== undefined && answers[q.id] !== "";
                  const isActive = i === active;
                  return (
                    <button key={i} onClick={() => setActive(i)} className="mono"
                      style={{
                        height: 38,
                        background: isActive ? "var(--blue-60)" : isAnswered ? "var(--g-100)" : "var(--surface)",
                        color: isActive ? "white" : isAnswered ? "var(--bg)" : "var(--text)",
                        border: "1px solid",
                        borderColor: isActive ? "var(--blue-60)" : isAnswered ? "var(--g-100)" : "var(--border-strong)",
                        fontSize: 13, fontWeight: 500, cursor: "pointer",
                      }}>
                      {i+1}{q.type === "essay" && <span style={{fontSize: 9}}>·E</span>}
                    </button>
                  );
                })}
              </div>
              <div className="divider"/>
              <div className="col gap-1 tiny">
                <div className="row gap-2"><span style={{width:10, height:10, background:"var(--g-100)"}}/><span className="muted">{lang==="id"?"Dijawab":"Answered"}</span></div>
                <div className="row gap-2"><span style={{width:10, height:10, background:"var(--surface)", border:"1px solid var(--border-strong)"}}/><span className="muted">{lang==="id"?"Belum":"Not answered"}</span></div>
                <div className="row gap-2"><span style={{width:10, height:10, background:"var(--blue-60)"}}/><span className="muted">{lang==="id"?"Aktif":"Active"}</span></div>
              </div>
            </div>
          </div>

          {isPretest && (
            <div className="banner">
              <Icon name="alert" color="var(--blue-70)"/>
              <div className="tiny" style={{flex:1, color:"var(--blue-80)"}}>
                {lang==="id"
                  ? "Pretest tidak memblokir akses materi. Lewati jika ragu — fokus pada belajar."
                  : "The pretest doesn't block access to materials. Skip if unsure — focus on learning."}
              </div>
            </div>
          )}

          {!isPretest && (
            <div className="banner amber">
              <Icon name="alert" color="var(--amber-50)"/>
              <div className="tiny" style={{flex:1, color:"var(--amber-ink, #6a4f00)"}}>
                {lang==="id"
                  ? "Skor minimum 70 untuk lulus. Esai dinilai trainer dalam 1–2 hari kerja."
                  : "Pass mark is 70. Essays are graded by trainers within 1–2 business days."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================
// LEARNING GAIN DASHBOARD (FR-6.8)
// =============================================================
function LearningGainScreen() {
  const t = useT();
  const { lang, role, navigate, currentProgram, currentProgramId, currentClass, currentClassId, user } = useApp();
  const isMobile = useIsMobile();
  const isStaff = role === "trainer" || role === "admin";

  // Personal view data — fetched from /tests/programs/:pid/learning-gain
  const [gain, setGain] = React.useState(null);
  React.useEffect(() => {
    if (!isStaff && currentProgramId) {
      getLearningGain(currentProgramId)
        .then(setGain)
        .catch((err) => {
          if (err?.response?.status !== 404) {
            toast.error(err?.response?.data?.error || err.message);
          }
        });
    }
  }, [currentProgramId, isStaff]);

  const me = {
    name: user?.fullName || "—",
    pretest: gain?.pretest ?? 0,
    posttest: gain?.posttest ?? 0,
  };
  // Nol karena belum mengerjakan berbeda dari nol karena semua jawaban salah.
  // Tanpa pembedaan ini, peserta yang posttest-nya belum dinilai akan dibilang
  // tidak lulus, padahal ia belum diuji sama sekali.
  const sudahPosttest = gain?.posttest != null;
  // Posttest boleh lebih rendah dari pretest — pretest tidak punya ambang
  // lulus, jadi gain negatif itu kasus normal, bukan yang boleh diabaikan.
  // Tanpa tanda kondisional ini, gain -65 tampil sebagai "+-65".
  const meGain = me.posttest - me.pretest;

  // Cohort view — fetched from /tests/programs/:pid/learning-gain/cohort (FR-7.5)
  const [cohortData, setCohortData] = React.useState(null);
  React.useEffect(() => {
    if (!isStaff || !currentProgramId) return;
    getCohortLearningGain(currentProgramId, currentClassId)
      .then(setCohortData)
      .catch((err) => {
        if (err?.response?.status !== 404) {
          toast.error(err?.response?.data?.error || err.message);
        }
      });
  }, [isStaff, currentProgramId, currentClassId]);

  // Esai yang menunggu dinilai. Dulu hanya ditampilkan jumlahnya (itu pun
  // dipatok "1"), tanpa satu pun cara membukanya — sementara backend sudah
  // punya endpoint penilaiannya sejak awal.
  const [pending, setPending] = React.useState([]);
  const [grading, setGrading] = React.useState(null); // attempt yang sedang dinilai
  const muatPending = React.useCallback(() => {
    if (!isStaff || !currentProgramId) return;
    getPendingGrading(currentProgramId, currentClassId).then(setPending).catch(() => setPending([]));
  }, [isStaff, currentProgramId, currentClassId]);
  React.useEffect(() => { muatPending() }, [muatPending]);

  // Peserta yang "Pending" (belum posttest sama sekali) — trainer bisa
  // catatkan skornya langsung, untuk kasus dikerjakan di luar sistem.
  const [manualEntry, setManualEntry] = React.useState(null); // { userId, name }

  function initialsOf(name) {
    return (name || "?").split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  }

  // Transform server participants → render shape used below
  const cohort = React.useMemo(() => {
    if (!cohortData?.participants) return [];
    return cohortData.participants.map((p) => ({
      userId: p.userId,
      i: initialsOf(p.name),
      n: p.name,
      pre: p.pretest != null ? Math.round(p.pretest) : null,
      post: p.posttest != null ? Math.round(p.posttest) : null,
    }));
  }, [cohortData]);

  const withGain = cohort.filter((c) => c.pre != null && c.post != null);
  const avgPre = cohortData?.avg?.pretest != null ? Math.round(cohortData.avg.pretest) : 0;
  const avgPost = cohortData?.avg?.posttest != null ? Math.round(cohortData.avg.posttest) : 0;
  const avgGain = cohortData?.avg?.gain != null ? Math.round(cohortData.avg.gain) : 0;

  // Client-side CSV of the cohort — keeps the button real without a second
  // backend round-trip. Dibangun dari `cohortData.participants` mentah (bukan
  // `cohort` yang sudah disederhanakan untuk render), supaya kolom email ikut
  // terbawa dan status "Belum pretest"/"Belum posttest" tetap jujur (tidak
  // menganggap skor yang belum ada sebagai 0 — bug yang sama seperti yang
  // dulu bikin gain fiktif tampil di tabel).
  function exportGainCsv() {
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      lang === "id" ? "Nama" : "Name",
      lang === "id" ? "Tempat Lahir" : "Place of Birth",
      lang === "id" ? "Tanggal Lahir" : "Date of Birth",
      "Email",
      "Pretest",
      "Posttest",
      "Gain",
      lang === "id" ? "Status" : "Status",
    ];
    const rows = (cohortData?.participants || []).map((p) => {
      const pre = p.pretest != null ? Math.round(p.pretest) : null;
      const post = p.posttest != null ? Math.round(p.posttest) : null;
      const gainVal = pre != null && post != null ? post - pre : null;
      const status =
        gainVal != null
          ? ""
          : post != null
            ? (lang === "id" ? "Tanpa baseline" : "No baseline")
            : (lang === "id" ? "Belum posttest" : "Pending posttest");
      return [p.name, p.placeOfBirth || "", p.dateOfBirth || "", p.email, pre ?? "", post ?? "", gainVal ?? "", status];
    });
    const csv = "﻿" + [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n") + "\r\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `learning-gain-${currentProgram?.code || currentProgramId || "program"}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Excel dan PDF butuh server (ExcelJS/Puppeteer untuk tata letak yang layak
  // dicetak) — CSV tetap client-side karena datanya sudah ada di `cohortData`.
  const [downloading, setDownloading] = React.useState(null);
  async function handleExport(kind) {
    if (kind === "learning-gain-csv") { exportGainCsv(); return; }
    setDownloading(kind);
    try {
      const fn = kind === "learning-gain-xlsx" ? downloadLearningGainXlsx : downloadLearningGainPdf;
      const blob = await fn(currentProgramId, currentClassId);
      const ext = kind.endsWith("xlsx") ? "xlsx" : "pdf";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `learning-gain-${currentProgram?.code || currentProgramId || "program"}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      if (err?.response?.status === 503) {
        toast.error(lang === "id" ? "Ekspor PDF sedang tidak tersedia" : "PDF export unavailable");
      } else {
        toast.error(err?.response?.data?.error || err.message);
      }
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {isStaff ? (lang === "id" ? "Trainer · Analitik" : "Trainer · Analytics") : (lang === "id" ? "Peserta · Hasil" : "Participant · Results")}
          </div>
          <h1 className="h1" style={{marginTop: 6}}>{t.learningGain}</h1>
          <div className="muted mt-1">
            {isStaff
              ? (lang==="id"
                ? "Perbandingan skor pretest dan posttest per peserta."
                : "Pretest vs posttest scores per participant.")
              : (lang==="id"
                ? "Perbandingan skor awal dan akhir Anda."
                : "Your baseline vs. final score — how much you learned.")}
          </div>
        </div>
        {isStaff && (
          <div className="page-header-actions">
            <ExportMenu
              formats={["csv", "xlsx", "pdf"]}
              kindPrefix="learning-gain"
              busyKind={downloading}
              onSelect={handleExport}
              lang={lang}
            />
          </div>
        )}
      </div>

      {/* Personal hero (participant view) */}
      {!isStaff && (
        <div className="card mb-4">
          <div style={{padding: "28px 32px"}}>
            <div className="row gap-5" style={{alignItems: "center", flexWrap: "wrap"}}>
              <div style={{flex: 1, minWidth: 220}}>
                <div className="tiny muted" style={{textTransform:"uppercase", letterSpacing:".08em"}}>{lang==="id"?"Skor Anda":"Your scores"}</div>
                <div className="row gap-4 mt-3" style={{alignItems: "flex-end", flexWrap: "wrap"}}>
                  <div>
                    <div className="tiny muted">{t.pretest}</div>
                    <div className="mono" style={{fontSize: 48, fontWeight: 300, letterSpacing: "-.02em", color: "var(--text-secondary)"}}>{me.pretest}</div>
                  </div>
                  <Icon name="arrow_right" size={20} color="var(--text-tertiary)"/>
                  <div>
                    <div className="tiny muted">{t.posttest}</div>
                    <div className="mono" style={{fontSize: 48, fontWeight: 400, letterSpacing: "-.02em", color: "var(--blue-70)"}}>{me.posttest}</div>
                  </div>
                  <div style={{marginLeft: 24}}>
                    <div className="tiny muted">{lang==="id"?"Gain":"Gain"}</div>
                    <div className="mono" style={{fontSize: 48, fontWeight: 400, color: meGain >= TARGET_GAIN ? "var(--green-60)" : meGain < 0 ? "var(--amber-50)" : "var(--text-secondary)"}}>{meGain >= 0 ? "+" : ""}{meGain}</div>
                  </div>
                </div>
              </div>
              {/* Big visual */}
              <div style={{width: "min(340px, 100%)"}}>
                <GainBars pre={me.pretest} post={me.posttest} lang={lang}/>
              </div>
            </div>
            <div className="divider"/>
            {/* Centang hanya bila syaratnya benar-benar terpenuhi.
                Sebelumnya kedua baris ini selalu hijau bercentang apa pun
                nilainya — peserta yang mendapat 40 tetap diberi tahu bahwa ia
                "Lulus posttest (≥ 70)". Untuk pelatihan yang menerbitkan
                sertifikat keselamatan, itu bukan cacat tampilan. */}
            <div className="row gap-5" style={{flexWrap: "wrap"}}>
              <Syarat
                terpenuhi={sudahPosttest && meGain >= TARGET_GAIN}
                menunggu={!sudahPosttest}
                label={lang==="id"?`Target gain ≥ ${TARGET_GAIN}`:`Target gain ≥ ${TARGET_GAIN}`}
                nilai={sudahPosttest ? `${meGain >= 0 ? "+" : ""}${meGain}` : "—"}
                lang={lang}
              />
              <Syarat
                terpenuhi={sudahPosttest && me.posttest >= NILAI_LULUS}
                menunggu={!sudahPosttest}
                label={lang==="id"?`Lulus posttest (≥ ${NILAI_LULUS})`:`Posttest pass (≥ ${NILAI_LULUS})`}
                nilai={sudahPosttest ? String(me.posttest) : "—"}
                lang={lang}
              />
            </div>
          </div>
        </div>
      )}

      {/* Cohort stats */}
      {isStaff && currentClass && (
        <div className="tiny muted mb-2">{lang==="id"?"Kelas":"Class"}: {currentClass.name}</div>
      )}
      {isStaff && (
        <div className="grid grid-4 mb-4">
          <Stat label={lang==="id"?"Rata-rata Pretest":"Average Pretest"} value={avgPre} hint={lang==="id"?"baseline batch":"batch baseline"}/>
          <Stat label={lang==="id"?"Rata-rata Posttest":"Average Posttest"} value={avgPost} delta={`vs pre +${avgGain}`} deltaDir="up"/>
          <Stat label={lang==="id"?"Rata-rata Gain":"Average Gain"} value={`+${avgGain}`} hint={lang==="id"?`sasaran ≥ ${TARGET_GAIN}`:`target ≥ ${TARGET_GAIN}`}/>
          <Stat label={lang==="id"?"Menunggu nilai":"Awaiting grade"} value={String(pending.length)} hint={lang==="id"?"esai dinilai trainer":"essays in trainer queue"}/>
        </div>
      )}

      {/* Antrean penilaian esai. Ditaruh di atas grafik karena inilah satu-satunya
          bagian halaman ini yang menuntut tindakan — sisanya bacaan. */}
      {isStaff && pending.length > 0 && (
        <div className="card mb-4">
          <div className="card-header">
            <div className="h3">{lang==="id"?"Esai menunggu penilaian":"Essays awaiting grading"}</div>
            <Badge tone="warning">{pending.length}</Badge>
          </div>
          <div style={{padding: "8px 16px 14px"}}>
            {pending.map((a, i) => (
              <div key={a.id} className="row gap-3" style={{
                alignItems: "center",
                padding: "10px 0",
                borderTop: i > 0 ? "1px solid var(--border)" : "none",
              }}>
                <div className="avatar" style={{width: 26, height: 26, fontSize: 10}}>
                  {initialsOf(a.user?.full_name)}
                </div>
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontSize: 13, fontWeight: 500}} className="text-truncate">
                    {a.user?.full_name || "—"}
                  </div>
                  <div className="muted" style={{fontSize: 11}}>
                    {a.test?.type === "PRETEST" ? "Pretest" : "Posttest"}
                    {a.submitted_at ? ` · ${new Date(a.submitted_at).toLocaleDateString(lang==="id"?"id-ID":"en-GB")}` : ""}
                  </div>
                </div>
                <Btn variant="primary" size="sm" onClick={() => setGrading(a)}>
                  {lang==="id"?"Nilai":"Grade"}
                </Btn>
              </div>
            ))}
          </div>
        </div>
      )}

      {grading && (
        <GradeEssayModal
          attempt={grading}
          lang={lang}
          onClose={() => setGrading(null)}
          onGraded={() => {
            setGrading(null);
            muatPending();
            // Nilai peserta baru saja berubah, jadi sebaran dan rata-rata di
            // halaman ini ikut basi bila tidak dimuat ulang.
            getCohortLearningGain(currentProgramId, currentClassId).then(setCohortData).catch(() => {});
          }}
        />
      )}

      {manualEntry && (
        <ManualScoreModal
          programId={currentProgramId}
          userId={manualEntry.userId}
          name={manualEntry.name}
          kind={manualEntry.kind}
          lang={lang}
          onClose={() => setManualEntry(null)}
          onSaved={() => {
            setManualEntry(null);
            getCohortLearningGain(currentProgramId, currentClassId).then(setCohortData).catch(() => {});
          }}
        />
      )}

      {/* Sebaran seangkatan — pandangan trainer, bukan peserta.
          Blok staf lain sudah dibungkus isStaff sejak awal; yang ini terlewat,
          sehingga peserta melihat dua kartu kosong beserta kalimat "belum ada
          peserta yang menyelesaikan pretest dan posttest" yang justru
          membantah skornya sendiri di layar yang sama. Datanya sendiri tidak
          pernah bocor: endpoint kohort hanya untuk trainer/admin, jadi bagi
          peserta cohort selalu kosong. */}
      {isStaff && (
      <div className="grid" style={{gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 16}}>
        {/* Distribution chart */}
        <div className="card">
          <div className="card-header">
            <div className="h3">{lang==="id"?"Distribusi Pretest vs Posttest":"Pretest vs Posttest distribution"}</div>
            <div className="row gap-3 small">
              <span className="row gap-1"><span style={{width:10, height:10, background:"var(--g-40)"}}/>Pretest</span>
              <span className="row gap-1"><span style={{width:10, height:10, background:"var(--blue-60)"}}/>Posttest</span>
            </div>
          </div>
          <div style={{padding: "20px 24px"}}>
            <div style={{display: "grid", gridTemplateColumns: "1fr", gap: 14}}>
              {cohort.map((c, i) => {
                // Gain butuh KEDUA skor. Sebelumnya hanya `c.post !== null`
                // yang dicek, jadi peserta tanpa pretest (c.pre === null)
                // ikut dihitung seolah pretest-nya 0 — JS mengubah
                // `post - null` jadi `post - 0`, dan angka gain palsu itu
                // tetap tampil hijau seakan peserta itu benar-benar naik
                // skor, padahal baseline-nya tidak pernah ada.
                const gain = c.pre != null && c.post != null ? c.post - c.pre : null;
                const tanpaBaseline = c.post != null && c.pre == null;
                return (
                  <div key={i} className="row gap-3" style={{alignItems: "center"}}>
                    <div className="avatar" style={{width: 24, height: 24, fontSize: 10}}>{c.i}</div>
                    <div style={{width: 110, fontSize: 13}} className="text-truncate">{c.n}</div>
                    <div style={{flex: 1, position: "relative", height: 22}}>
                      {/* Background scale */}
                      <div style={{position: "absolute", inset: "10px 0 10px 0", background: "var(--g-10)"}}/>
                      {/* 70 threshold line */}
                      <div style={{position: "absolute", left: "70%", top: 0, bottom: 0, width: 1, background: "var(--g-40)", borderLeft: "1px dashed var(--g-40)"}}/>
                      {/* Pretest bar */}
                      {c.pre != null && (
                        <div style={{position: "absolute", top: 2, left: 0, height: 8, width: `${c.pre}%`, background: "var(--g-40)"}}/>
                      )}
                      {/* Posttest bar */}
                      {c.post !== null && (
                        <div style={{position: "absolute", top: 12, left: 0, height: 8, width: `${c.post}%`, background: gain == null ? "var(--g-40)" : gain >= 30 ? "var(--green-60)" : gain >= 15 ? "var(--blue-60)" : "var(--amber-50)"}}/>
                      )}
                      {c.post === null && (
                        <div style={{position: "absolute", top: 12, left: 0, height: 8, width: `100%`, background: "repeating-linear-gradient(135deg, var(--g-10) 0 4px, var(--g-20) 4px 8px)"}}/>
                      )}
                    </div>
                    <div
                      className="mono small tnum"
                      style={{width: 30, textAlign: "right", color: "var(--text-secondary)", cursor: c.pre == null ? "pointer" : undefined}}
                      title={c.pre == null ? (lang==="id"?"Catat skor pretest manual":"Enter pretest score manually") : undefined}
                      onClick={c.pre == null ? () => setManualEntry({ userId: c.userId, name: c.n, kind: "pretest" }) : undefined}
                    >{c.pre != null ? c.pre : "—"}</div>
                    <Icon name="arrow_right" size={10} color="var(--text-tertiary)"/>
                    <div className="mono small tnum" style={{width: 30, textAlign: "right"}}>{c.post !== null ? c.post : "—"}</div>
                    <div style={{width: 60, textAlign: "right"}}>
                      {gain !== null ? (
                        <span className={"tag tiny " + (gain >= 30 ? "green" : gain >= 15 ? "blue" : "amber")}>{gain >= 0 ? "+" : ""}{gain}</span>
                      ) : tanpaBaseline ? (
                        <span
                          className="tag outline tiny"
                          title={lang==="id"?"Tidak ada skor pretest — klik untuk mencatat manual":"No pretest score — click to enter manually"}
                          style={{cursor: "pointer"}}
                          onClick={() => setManualEntry({ userId: c.userId, name: c.n, kind: "pretest" })}
                        >
                          {lang==="id"?"Tanpa baseline":"No baseline"}
                        </span>
                      ) : (
                        <span
                          className="tag outline tiny"
                          title={lang==="id"?"Catat skor posttest manual":"Enter posttest score manually"}
                          style={{cursor: "pointer"}}
                          onClick={() => setManualEntry({ userId: c.userId, name: c.n, kind: "posttest" })}
                        >
                          {lang==="id"?"Pending":"Pending"}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="divider"/>
            <div className="row between tiny muted">
              <span>0</span><span>25</span><span>50</span><span>70 (lulus)</span><span>100</span>
            </div>
          </div>
        </div>

        {/* Side panel */}
        <div className="col gap-3">
          <div className="card">
            <div className="card-header"><div className="h3">{lang==="id"?"Distribusi gain":"Gain distribution"}</div></div>
            <div style={{padding: "16px 20px"}}>
              {[
                {l: lang==="id"?"Gain tinggi (≥ 30)":"High gain (≥ 30)", n: withGain.filter(c => c.post - c.pre >= 30).length, c: "var(--green-60)"},
                {l: lang==="id"?"Gain sedang (15–29)":"Mid gain (15–29)", n: withGain.filter(c => c.post - c.pre >= 15 && c.post - c.pre < 30).length, c: "var(--blue-60)"},
                {l: lang==="id"?"Gain rendah (< 15)":"Low gain (< 15)", n: withGain.filter(c => c.post - c.pre < 15).length, c: "var(--amber-50)"},
                {l: lang==="id"?"Belum posttest":"Pending posttest", n: cohort.length - withGain.length, c: "var(--g-40)"},
              ].map((r, i) => (
                <div key={i} className="mb-3">
                  <div className="row between mb-1">
                    <span className="small">{r.l}</span>
                    <span className="mono small tnum">{r.n}</span>
                  </div>
                  <div style={{height: 6, background: "var(--g-10)"}}>
                    {/* Tanpa penjaga ini, program tanpa peserta menghasilkan
                        0/0 = NaN, dan `width: NaN%` bukan CSS yang sah — jadi
                        peramban mengabaikannya dan batangnya justru memenuhi
                        lebar penuh, seolah semua kategori 100% padahal
                        angkanya nol. */}
                    <div style={{height: "100%", width: cohort.length ? `${(r.n / cohort.length) * 100}%` : "0%", background: r.c}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Kartu "Topik dengan kenaikan terbesar" dihapus: isinya empat baris
              yang dipatok di kode (+48/+36/+28/+14), bukan dari data mana pun.
              Nama topiknya pun khas OPRC — di platform yang melayani banyak
              lembaga, sebuah rumah sakit akan melihat "Pemilihan boom".
              Menghitungnya sungguhan menuntut penilaian per topik, yang
              soal-soalnya belum punya. */}

          {/* Spanduk ini dulu selalu hijau bertuliskan "tercapai", bahkan saat
              gain rata-rata +0 — sambil menampilkan target ≥ 20 di kalimat yang
              sama. "PRD" juga istilah dokumen internal pengembang; trainer tidak
              punya cara tahu artinya. */}
          {withGain.length === 0 ? (
            <div className="banner">
              <Icon name="info" color="var(--text-3)"/>
              <div className="small" style={{flex: 1}}>
                {lang==="id"
                  ? "Belum ada peserta yang menyelesaikan pretest dan posttest, jadi gain batch belum bisa dihitung."
                  : "No participant has completed both pretest and posttest yet, so batch gain cannot be computed."}
              </div>
            </div>
          ) : avgGain >= TARGET_GAIN ? (
            <div className="banner green">
              <Icon name="check" color="var(--green-60)"/>
              <div className="small" style={{flex: 1}}>
                <strong>{lang==="id"?"Target kenaikan tercapai.":"Gain target met."}</strong>{" "}
                {lang==="id"
                  ? `Gain rata-rata batch +${avgGain}, dari sasaran ≥ ${TARGET_GAIN}.`
                  : `Batch average gain +${avgGain}, against a target of ≥ ${TARGET_GAIN}.`}
              </div>
            </div>
          ) : (
            <div className="banner amber">
              <Icon name="info" color="var(--amber)"/>
              <div className="small" style={{flex: 1}}>
                <strong>{lang==="id"?"Target kenaikan belum tercapai.":"Gain target not met yet."}</strong>{" "}
                {lang==="id"
                  ? `Gain rata-rata batch +${avgGain}, sasarannya ≥ ${TARGET_GAIN}. Sasaran ini acuan mutu, bukan syarat kelulusan.`
                  : `Batch average gain +${avgGain}, target is ≥ ${TARGET_GAIN}. This is a quality benchmark, not a pass requirement.`}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
}

// Bars used in participant hero.
//
// Dulu ada empat baris: skor peserta DAN rata-rata angkatan untuk tiap tes,
// masing-masing dengan label "Anda"/"Rata-rata". Tapi labelnya tidak pernah
// dirender — hanya nama tesnya — sehingga peserta melihat
// "Pretest / Pretest / Posttest / Posttest" tanpa cara membedakannya. Lebih
// buruk lagi, rata-rata angkatan SELALU nol di sini: angkanya berasal dari
// endpoint kohort yang hanya boleh diakses trainer/admin, jadi peserta tidak
// pernah mengambilnya. Dua baris itu menampilkan "0" yang tampak seperti nilai
// nol sungguhan, tepat di layar tempat peserta mencari nilainya sendiri.
//
// Jadi baris rata-rata dihapus, bukan diperbaiki labelnya: datanya memang tidak
// terjangkau dari sisi peserta. Menghidupkannya kembali menuntut endpoint baru
// yang aman dibaca peserta — bukan sekadar menambahkan label.
function GainBars({ pre, post, lang }) {
  const max = 100;
  return (
    <div className="col gap-2">
      {[
        {l: lang==="id"?"Pretest":"Pretest", v: pre, c: "var(--g-40)"},
        {l: lang==="id"?"Posttest":"Posttest", v: post, c: "var(--blue-60)"},
      ].map((b, i) => (
        <div key={i} className="row gap-2" style={{alignItems: "center"}}>
          <div className="tiny mono" style={{width: 60, color: "var(--text-secondary)"}}>{b.l}</div>
          <div style={{flex: 1, height: 16, background: "var(--g-10)", position: "relative"}}>
            <div style={{position: "absolute", inset: "0 auto 0 0", width: `${(b.v / max) * 100}%`, background: b.c}}/>
            <div style={{position: "absolute", left: "70%", top: 0, bottom: 0, width: 1, borderLeft: "1px dashed var(--g-40)"}}/>
          </div>
          <div className="mono small" style={{width: 30, textAlign: "right", color: "var(--text)"}}>{b.v}</div>
        </div>
      ))}
    </div>
  );
}

// =============================================================
// GRADE ESSAY MODAL — FR-7.4
// =============================================================
//
// Backend sudah punya endpoint penilaiannya sejak awal, tetapi tidak ada satu
// pun layar yang memanggilnya: trainer melihat esai menumpuk di antrean tanpa
// cara membukanya. Ini bagian yang hilang itu.
//
// Soal pilihan ganda ikut ditampilkan (terkunci, hanya sebagai konteks) karena
// nilai akhirnya gabungan keduanya — trainer perlu tahu peserta sudah
// mengumpulkan berapa poin sebelum memutuskan nilai esainya.
function GradeEssayModal({ attempt, lang, onClose, onGraded }) {
  const [full, setFull] = React.useState(null);
  const [grades, setGrades] = React.useState({});   // { questionId: "poin" }
  const [busy, setBusy] = React.useState(false);
  const [hasil, setHasil] = React.useState(null);

  React.useEffect(() => {
    getAttempt(attempt.id)
      .then(setFull)
      .catch((e) => toast.error(e?.response?.data?.error || e.message));
  }, [attempt.id]);

  const soal = full?.test?.questions || [];
  const jawaban = full?.answers || {};
  const esai = soal.filter((q) => q.type === "ESSAY");
  const pg = soal.filter((q) => q.type === "MCQ");

  const poinPG = pg.reduce((n, q) => n + (jawaban[q.id] === q.correct_answer ? q.points : 0), 0);
  const totalPoin = soal.reduce((n, q) => n + q.points, 0);

  // Nilai yang belum diisi dihitung nol, sama seperti perlakuan backend —
  // jadi angka pratinjau di sini tidak akan berbeda dari hasil sebenarnya.
  const poinEsai = esai.reduce((n, q) => {
    const v = Number(grades[q.id]);
    return n + (Number.isFinite(v) ? Math.min(Math.max(0, v), q.points) : 0);
  }, 0);
  const skorPratinjau = totalPoin > 0 ? Math.round(((poinPG + poinEsai) / totalPoin) * 100) : 0;

  // Kosong bukan nol: menyimpan tanpa mengisi berarti memberi nol tanpa sadar,
  // dan nilai itu langsung dikirim ke peserta lewat notifikasi.
  const belumDiisi = esai.filter((q) => {
    const v = grades[q.id];
    return v === undefined || v === "" || !Number.isFinite(Number(v));
  });

  async function simpan() {
    setBusy(true);
    try {
      const payload = {};
      for (const q of esai) payload[q.id] = Math.min(Math.max(0, Number(grades[q.id])), q.points);
      const r = await gradeEssays(attempt.id, payload);
      setHasil(r);
      toast.success(lang === "id" ? "Nilai tersimpan" : "Grade saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"grid", placeItems:"center", zIndex:50, padding:16}}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{width:"min(680px, 100%)", maxHeight:"90vh", display:"flex", flexDirection:"column", background:"var(--surface)"}}
      >
        <div className="card-header">
          <div>
            <div className="h3">{lang==="id"?"Nilai esai":"Grade essay"}</div>
            <div className="muted" style={{fontSize:12}}>
              {attempt.user?.full_name} · {attempt.test?.type === "PRETEST" ? "Pretest" : "Posttest"}
            </div>
          </div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>

        {hasil ? (
          <div className="col gap-3" style={{padding:20}}>
            <div className="banner green">
              <Icon name="check" color="var(--green-60)"/>
              <div className="small" style={{flex:1}}>
                {lang==="id"
                  ? <>Nilai akhir <strong>{Math.round(hasil.score)}</strong> tersimpan ({hasil.earned}/{hasil.total} poin). Peserta sudah diberi tahu.</>
                  : <>Final score <strong>{Math.round(hasil.score)}</strong> saved ({hasil.earned}/{hasil.total} points). The participant has been notified.</>}
              </div>
            </div>
            <div style={{display:"flex", justifyContent:"flex-end"}}>
              <Btn variant="primary" onClick={onGraded}>{lang==="id"?"Selesai":"Done"}</Btn>
            </div>
          </div>
        ) : !full ? (
          <div className="empty small" style={{padding:32}}>{lang==="id"?"Memuat jawaban…":"Loading answers…"}</div>
        ) : (
          <>
            <div style={{padding:"0 20px", overflowY:"auto", flex:1}}>
              {pg.length > 0 && (
                <div className="muted" style={{fontSize:12, padding:"12px 0"}}>
                  {lang==="id"
                    ? `Pilihan ganda sudah dinilai otomatis: ${poinPG} dari ${pg.reduce((n,q)=>n+q.points,0)} poin.`
                    : `Multiple-choice auto-graded: ${poinPG} of ${pg.reduce((n,q)=>n+q.points,0)} points.`}
                </div>
              )}

              {esai.length === 0 && (
                <div className="empty small" style={{padding:24}}>
                  {lang==="id"?"Tidak ada soal esai pada percobaan ini.":"This attempt has no essay questions."}
                </div>
              )}

              {esai.map((q, i) => {
                const isi = (jawaban[q.id] ?? "").toString().trim();
                return (
                  <div key={q.id} style={{padding:"14px 0", borderTop: i>0?"1px solid var(--border)":"none"}}>
                    <div style={{fontSize:13, fontWeight:500, marginBottom:8}}>
                      {i+1}. {q.body}
                    </div>
                    <div style={{
                      background:"var(--g-10)", borderRadius:6, padding:"12px 14px",
                      fontSize:13, lineHeight:1.6, whiteSpace:"pre-wrap",
                      color: isi ? "var(--text)" : "var(--text-3)",
                    }}>
                      {isi || (lang==="id"?"(tidak dijawab)":"(not answered)")}
                    </div>
                    <div className="row gap-2" style={{alignItems:"center", marginTop:10}}>
                      <span className="small muted">{lang==="id"?"Poin":"Points"}</span>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={q.points}
                        step={1}
                        value={grades[q.id] ?? ""}
                        placeholder="—"
                        onChange={(e) => setGrades((g) => ({...g, [q.id]: e.target.value}))}
                        style={{width:90, height:32, fontSize:14}}
                      />
                      <span className="small muted">{lang==="id"?`dari ${q.points}`:`of ${q.points}`}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card-header" style={{borderTop:"1px solid var(--border)", borderBottom:"none"}}>
              <div className="small muted">
                {lang==="id"?"Nilai akhir":"Final score"}{" "}
                <strong className="mono" style={{color:"var(--text)", fontSize:15}}>{skorPratinjau}</strong>
                <span style={{opacity:.7}}>/100</span>
              </div>
              <div className="row gap-2">
                <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                  {lang==="id"?"Batal":"Cancel"}
                </Btn>
                <Btn
                  variant="primary"
                  size="sm"
                  onClick={simpan}
                  disabled={busy || esai.length === 0 || belumDiisi.length > 0}
                >
                  {busy
                    ? (lang==="id"?"Menyimpan…":"Saving…")
                    : (lang==="id"?"Simpan nilai":"Save grade")}
                </Btn>
              </div>
            </div>
            {belumDiisi.length > 0 && (
              <div className="field-hint" style={{padding:"0 20px 14px", color:"var(--text-3)"}}>
                {lang==="id"
                  ? `Isi poin untuk ${belumDiisi.length} soal lagi. Dikosongkan berarti nol, dan nilainya langsung dikirim ke peserta.`
                  : `${belumDiisi.length} more to score. Leaving one blank means zero, and the grade is sent to the participant immediately.`}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Trainer/admin mencatat skor pretest/posttest langsung untuk peserta yang
// belum punya skor — mis. dikerjakan di kertas, atau peserta tidak bisa
// akses aplikasi. `kind`: "pretest" | "posttest".
function ManualScoreModal({ programId, userId, name, kind = "posttest", lang, onClose, onSaved }) {
  const isPre = kind === "pretest";
  const [score, setScore] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const nilai = Number(score);
  const valid = score !== "" && Number.isFinite(nilai) && nilai >= 0 && nilai <= 100;

  async function simpan() {
    setBusy(true);
    try {
      await manualScoreTest(programId, kind, userId, nilai);
      toast.success(lang === "id" ? "Skor tersimpan" : "Score saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{position:"fixed", inset:0, background:"rgba(0,0,0,.5)", display:"grid", placeItems:"center", zIndex:50, padding:16}}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{width:"min(420px, 100%)", background:"var(--surface)"}}
      >
        <div className="card-header">
          <div>
            <div className="h3">
              {isPre
                ? (lang==="id"?"Catat skor pretest":"Enter pretest score")
                : (lang==="id"?"Catat skor posttest":"Enter posttest score")}
            </div>
            <div className="muted" style={{fontSize:12}}>{name}</div>
          </div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>
        <div style={{padding:20}}>
          <div className="small muted" style={{marginBottom:12}}>
            {isPre
              ? (lang==="id"
                  ? "Untuk peserta yang mengerjakan di luar aplikasi (mis. kertas) atau tidak bisa mengakses sistem. Skor ini menjadi baseline pretest peserta."
                  : "For participants who took the test outside the app (e.g. on paper) or can't access the system. This becomes the participant's pretest baseline.")
              : (lang==="id"
                  ? "Untuk peserta yang mengerjakan di luar aplikasi (mis. kertas) atau tidak bisa mengakses sistem. Skor ini menggantikan status \"Pending\" dan langsung berlaku untuk kelayakan sertifikat."
                  : "For participants who took the test outside the app (e.g. on paper) or can't access the system. This replaces the \"Pending\" status and immediately counts toward certificate eligibility.")}
          </div>
          <div className="row gap-2" style={{alignItems:"center"}}>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              step={1}
              autoFocus
              value={score}
              placeholder="0–100"
              onChange={(e) => setScore(e.target.value)}
              style={{width:100, height:36, fontSize:15}}
            />
            <span className="small muted">/100</span>
          </div>
        </div>
        <div className="card-header" style={{borderTop:"1px solid var(--border)", borderBottom:"none"}}>
          <div />
          <div className="row gap-2">
            <Btn variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              {lang==="id"?"Batal":"Cancel"}
            </Btn>
            <Btn variant="primary" size="sm" onClick={simpan} disabled={busy || !valid}>
              {busy ? (lang==="id"?"Menyimpan…":"Saving…") : (lang==="id"?"Simpan":"Save")}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

export { TestScreen, LearningGainScreen }
